import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { CheckIcon, InfoCircleIcon } from './icons';
import type { DomainCheckStatus } from './useDomainCheck';

/**
 * Pill que se renderiza debajo del email input cuando el live check tiene resultado.
 *
 * Variants:
 * - authorized (verde): "Authorized domain · @acme.com"
 * - unauthorized (amber): "@acme.com is not authorized for this portal"
 * - idle / checking: no se renderiza (return null)
 */
export function DomainHintPill({ status, domain }: { status: DomainCheckStatus; domain: string }) {
  const { t } = useTranslation();

  if (status === 'idle' || status === 'checking') return null;
  if (!domain) return null;

  const authorized = status === 'authorized';
  const color = authorized ? tokens.color.accent : tokens.color.warn;
  const bg = authorized ? tokens.color.accentSoft : tokens.color.warnSoft;
  const border = authorized ? '#d6e4fb' : '#f3e1c4';
  const Icon = authorized ? CheckIcon : InfoCircleIcon;
  const label = authorized
    ? t('login.field.email.hint.authorized', { domain })
    : t('login.field.email.hint.unauthorized', { domain });

  return (
    <Box
      data-testid={authorized ? 'login-domain-hint-authorized' : 'login-domain-hint-unauthorized'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        mt: '10px',
        px: '12px',
        py: '8px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: '9px',
        fontFamily: tokens.font.mono,
        fontSize: 12,
        color,
      }}
    >
      <Icon style={{ width: 12, height: 12, flexShrink: 0 }} />
      <span>{label}</span>
    </Box>
  );
}
